import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "./api.js";
import type { Notification } from "@lakseborsen/shared";

interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  reload: () => void;
}

export function useNotifications(
  getToken: () => Promise<string>,
  isAuthenticated: boolean,
): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const fetchingRef = useRef(false);

  const load = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const data = await apiFetch<Notification[]>("/notifications", {
        getToken,
      });
      setNotifications(data);
    } catch {
      // silently ignore — user may not be authenticated yet
    } finally {
      fetchingRef.current = false;
    }
  }, [getToken]);

  // Only fetch when authenticated; re-fetch when auth state becomes true
  useEffect(() => {
    if (!isAuthenticated) return;
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load, isAuthenticated]);

  const markRead = useCallback(
    async (id: string) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      );
      try {
        await apiFetch(`/notifications/${id}/read`, {
          method: "PATCH",
          getToken,
        });
      } catch {
        // re-fetch to sync if optimistic update failed
        load();
      }
    },
    [getToken, load],
  );

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: now })),
    );
    try {
      await apiFetch("/notifications/read-all", {
        method: "POST",
        getToken,
      });
    } catch {
      load();
    }
  }, [getToken, load]);

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  return { notifications, unreadCount, markRead, markAllRead, reload: load };
}
